// A consistent way to report that an action failed.
//
// Several screens wrapped a purchase or a save in `try { ... } finally { ... }`
// with no `catch`. The finally reset the spinner and closed the dialog, so a
// failed request looked exactly like a successful one that did nothing: the
// button un-pressed itself and the customer was left staring at the same page.
// On the buy-now paths that is the worst possible place to fail quietly.
//
// `useActionError` + `<ActionError/>` make handling it a two-line change, so
// there is no incentive to skip it.
import React, { useCallback, useState } from "react";
import { Alert, Button, Snackbar } from "@mui/material";

/**
 * @param {string} [fallback] Message when the error carries none of its own.
 */
export const useActionError = (fallback = "Something went wrong. Please try again.") => {
  const [error, setError] = useState("");

  const report = useCallback(
    (err) => {
      const data = err?.response?.data;
      const message = Array.isArray(data?.errors)
        ? data.errors.join(" ")
        : data?.error || data?.message;
      setError(message || fallback);
    },
    [fallback]
  );

  return { error, report, clear: useCallback(() => setError(""), []) };
};

/**
 * @param {Object} props
 * @param {string} props.error
 * @param {() => void} props.onClose
 * @param {() => void} [props.onRetry]
 */
const ActionError = ({ error, onClose, onRetry }) => (
  <Snackbar
    open={!!error}
    autoHideDuration={10000}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
  >
    <Alert
      severity="error"
      onClose={onClose}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
      sx={{ borderRadius: 2, maxWidth: 520 }}
    >
      {error}
    </Alert>
  </Snackbar>
);

export default ActionError;
